import { useEffect, useMemo } from 'react';
import {
  MemoryRouter,
  UNSAFE_LocationContext as RouterLocationContext,
  UNSAFE_RouteContext as RouterRouteContext
} from 'react-router-dom';
import { configureFinanceClient } from './api/financeClient';
import { FinanceContext, APPROVE_ROLES, EDIT_ROLES } from './financeContext';
import { wordingFor } from './wording';
import { DetailModalsProvider } from './components/detailModals';
import FinanceRoutes from './routes';
import './finance.css';

// The mountable root of the whole finance UI (spec §A1). Takes everything it needs via props —
// it NEVER imports from ../ or a vertical app.
//
//   <FinanceApp
//     apiBaseUrl={API_BASE}          axios baseURL
//     getToken={() => token}         bearer token getter
//     vertical="car_rental"          active vertical — wording only
//     actorRole={user?.role}         role gates (optional; standalone can derive from a /me call)
//     onOpenExternal={(kind, e)=>…}   rare jump back into the host app
//     renderVisitActions={(v, close) => <VisitActions .../>}  host-supplied action strip for the
//                                     shared VisitDetailsModal (Part C) — admin/owner only
//     embedded                       default true → own MemoryRouter; false → host owns the router
//   />
export default function FinanceApp({
  apiBaseUrl,
  getToken,
  vertical = 'car_rental',
  actorRole = null,
  onOpenExternal = null,
  renderVisitActions = null,
  basename = '/',
  embedded = true
}) {
  configureFinanceClient({ apiBaseUrl, getToken });
  useEffect(() => {
    configureFinanceClient({ apiBaseUrl, getToken });
  }, [apiBaseUrl, getToken]);

  const ctx = useMemo(() => ({
    vertical,
    // Embedded in a vertical admin app -> scope every list to that vertical. Standalone
    // (embedded={false}) keeps the platform-wide view.
    scopedVertical: embedded ? vertical : null,
    wording: wordingFor(vertical),
    actorRole,
    canApproveFinance: APPROVE_ROLES.includes(actorRole),
    canEditConfig: EDIT_ROLES.includes(actorRole),
    isAdmin: actorRole === 'admin',
    onOpenExternal
  }), [vertical, embedded, actorRole, onOpenExternal]);

  const tree = (
    <FinanceContext.Provider value={ctx}>
      <DetailModalsProvider renderVisitActions={renderVisitActions}>
        <FinanceRoutes />
      </DetailModalsProvider>
    </FinanceContext.Provider>
  );

  if (embedded) {
    // The finance module runs its own isolated in-memory router so internal navigation
    // (/pending, /visit-fees, …) never touches the host app's URL. React Router forbids a
    // <Router> inside another <Router>, so we blank out the host's router contexts for this
    // subtree first — the nested <MemoryRouter> then initialises a fresh, self-contained
    // history. Standalone mode skips this entirely (the shell supplies the only <BrowserRouter>).
    return (
      <RouterRouteContext.Provider value={{ outlet: null, matches: [], isDataRoute: false }}>
        <RouterLocationContext.Provider value={null}>
          <MemoryRouter initialEntries={['/pending']}>{tree}</MemoryRouter>
        </RouterLocationContext.Provider>
      </RouterRouteContext.Provider>
    );
  }
  // Standalone: the host (src/index.js) supplies <BrowserRouter basename={…}>.
  return tree;
}
